import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FolderLock, Plus, Loader2, X, ShieldCheck, Clock, Ban, Archive,
} from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const ROOM_STATUS = {
  DRAFT: { label: "Bozza", cls: "bg-slate-100 text-slate-700" },
  UNDER_REVIEW: { label: "In revisione", cls: "bg-amber-100 text-amber-800" },
  ACTIVE: { label: "Attiva", cls: "bg-green-100 text-green-800" },
  EXPIRED: { label: "Scaduta", cls: "bg-slate-200 text-slate-600" },
  REVOKED: { label: "Revocata", cls: "bg-red-100 text-red-800" },
  ARCHIVED: { label: "Archiviata", cls: "bg-slate-100 text-slate-500" },
};
export const PURPOSES = {
  BANK_DUE_DILIGENCE: "Due diligence banca", CUSTOMER_REQUEST: "Richiesta cliente",
  SUPPLY_CHAIN: "Catena di fornitura", AUDIT: "Audit / Revisione",
  INVESTOR: "Investitore", PROCUREMENT: "Procurement", ESG_REVIEW: "Revisione ESG",
  OTHER: "Altro",
};
const EXPIRY_OPTIONS = [7, 15, 30, 60];

export default function DataRoom() {
  const { companyId, period, can } = useTenant();
  const navigate = useNavigate();
  const canCreate = can("dataroom:create");
  const [rooms, setRooms] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [form, setForm] = useState({ name: "", purpose: "BANK_DUE_DILIGENCE",
    expiry_days: 30, access_mode: "MAGIC_LINK", require_terms: false });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const { data } = await api.get(
        `/companies/${companyId}/periods/${period.id}/data-rooms`);
      setRooms(data.rooms);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/companies/${companyId}/periods/${period.id}/data-rooms`, form);
      toast.success("Data Room creata in bozza: aggiungi destinatari e contenuti");
      setShowWizard(false);
      navigate(`/data-room-interna/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="dataroom-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
            ESG Data Room
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Condividi report, dati ed evidenze con banche, clienti e auditor in modo
            selettivo, temporaneo e tracciato. Niente viene condiviso automaticamente.
          </p>
        </div>
        {canCreate && (
          <Button size="sm" className="h-8 text-xs bg-green-700 hover:bg-green-800 text-white"
            onClick={() => setShowWizard(true)} data-testid="create-dataroom-btn">
            <Plus className="w-3 h-3 mr-1" aria-hidden="true" />Nuova Data Room
          </Button>
        )}
      </div>

      {!rooms ? (
        <p className="text-sm text-slate-500">Caricamento…</p>
      ) : rooms.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-8 text-center" data-testid="dataroom-empty">
            <FolderLock className="w-8 h-8 text-slate-300 mx-auto mb-3" aria-hidden="true" />
            <p className="text-sm text-slate-600">
              Nessuna Data Room per questo periodo. Creane una per condividere contenuti
              ESG con soggetti esterni autorizzati.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="dataroom-list">
          {rooms.map((r) => {
            const st = ROOM_STATUS[r.status] || ROOM_STATUS.DRAFT;
            return (
              <Card key={r.id}
                className="border-slate-200 hover:border-green-300 transition-colors cursor-pointer"
                onClick={() => navigate(`/data-room-interna/${r.id}`)}
                data-testid={`dataroom-row-${r.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-sky-100 text-sky-800">
                      {r.status === "ACTIVE" ? <ShieldCheck className="w-5 h-5" aria-hidden="true" />
                        : r.status === "REVOKED" ? <Ban className="w-5 h-5" aria-hidden="true" />
                        : r.status === "ARCHIVED" ? <Archive className="w-5 h-5" aria-hidden="true" />
                        : <FolderLock className="w-5 h-5" aria-hidden="true" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-bold text-slate-900">{r.name}</p>
                        <Badge className={st.cls} data-testid={`dataroom-status-${r.id}`}>
                          {st.label}</Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {PURPOSES[r.purpose] || r.purpose}
                        {r.expires_at && ` · Scade il ${new Date(r.expires_at).toLocaleDateString("it-IT")}`}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-600">
                        <span data-testid={`dataroom-items-${r.id}`}>{r.items_count} contenuti</span>
                        <span data-testid={`dataroom-recipients-${r.id}`}>
                          {r.recipients_count} destinatari</span>
                        <span>v{r.content_version || 1}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {showWizard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog" aria-modal="true" data-testid="dataroom-wizard">
          <Card className="w-full max-w-md">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <CardTitle className="text-base">Nuova Data Room</CardTitle>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                  onClick={() => setShowWizard(false)} data-testid="wizard-close">
                  <X className="w-4 h-4" aria-hidden="true" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs text-slate-600">Nome</label>
                <input type="text" value={form.name} maxLength={200}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Es. Data Room Banca Demo — cessione credito"
                  className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm"
                  data-testid="wizard-name" aria-label="Nome Data Room" />
              </div>
              <div>
                <label className="text-xs text-slate-600">Scopo</label>
                <Select value={form.purpose}
                  onValueChange={(v) => setForm({ ...form, purpose: v })}>
                  <SelectTrigger data-testid="wizard-purpose" aria-label="Scopo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PURPOSES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-600">Durata accesso</label>
                  <Select value={String(form.expiry_days)}
                    onValueChange={(v) => setForm({ ...form, expiry_days: Number(v) })}>
                    <SelectTrigger data-testid="wizard-expiry" aria-label="Durata">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPIRY_OPTIONS.map((d) => (
                        <SelectItem key={d} value={String(d)}>{d} giorni</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-slate-600">Verifica destinatario</label>
                  <Select value={form.access_mode}
                    onValueChange={(v) => setForm({ ...form, access_mode: v })}>
                    <SelectTrigger data-testid="wizard-access-mode" aria-label="Modalità accesso">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MAGIC_LINK">Solo link sicuro</SelectItem>
                      <SelectItem value="MAGIC_LINK_OTP">Link + codice OTP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input type="checkbox" checked={form.require_terms}
                  onChange={(e) => setForm({ ...form, require_terms: e.target.checked })}
                  data-testid="wizard-terms" />
                Richiedi accettazione dei termini di riservatezza prima dell'accesso
              </label>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" className="h-8 text-xs"
                  onClick={() => setShowWizard(false)} data-testid="wizard-cancel">Annulla</Button>
                <Button size="sm" className="h-8 text-xs bg-green-700 hover:bg-green-800 text-white"
                  disabled={!form.name.trim() || busy} onClick={create}
                  data-testid="wizard-create">
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
                    : "Crea bozza"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
